import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import { AppDispatch, RootState } from '../../store/store';
import { lookupProviderByIdApi } from '../../rtk-apis/hl7/lookupProviderById';
import { lookupProviderByNameApi } from '../../rtk-apis/hl7/lookupProviderByName';
import { sendHL7Api } from '../../rtk-apis/hl7/sendHL7';
import { saveDraftDistributionApi } from '../../rtk-apis/hl7/saveDraftDistribution';
import { sendEmailDistributionApi } from '../../rtk-apis/distribution/sendEmailDistribution';
import { saveEmailDraftApi } from '../../rtk-apis/distribution/saveEmailDraft';
import { clearSendHL7State } from '../../store/slices/hl7/sendHL7';
import { clearSaveDraftDistributionState } from '../../store/slices/hl7/saveDraftDistribution';
import { clearSendEmailDistributionState } from '../../store/slices/distribution/sendEmailDistribution';
import { clearSaveEmailDraftState } from '../../store/slices/distribution/saveEmailDraft';
import { clearLookupProviderState } from '../../store/slices/hl7/lookupProviderByName';
import { clearLookupProviderByIdState } from '../../store/slices/hl7/lookupProviderById';
import { getToken } from '../../Utils/authUtils';
import { HL7 } from '../../constant';
import { downloadReportFile } from '../../Utils/downloadReportFile';
import { AU_PROVIDER_NUMBER, nameWords, parseReferredBy } from './referredByName';

/**
 * The recipient's name from a stored draft, in parts.
 *
 * Drafts saved since the parts were added carry them; older records hold only the
 * display string, which has to be split. The split mirrors parseDoctorNameLocal in
 * hl7Controller — last word is the surname — so a draft sent from the row menu and the
 * same draft sent after editing produce the same PV1-8.
 */
function splitStoredProviderName(draft: {
  toProviderFirstName?: string;
  toProviderLastName?: string;
  toProviderName?: string;
}): { firstName: string; lastName: string } {
  if (draft.toProviderFirstName || draft.toProviderLastName) {
    return {
      firstName: draft.toProviderFirstName || '',
      lastName: draft.toProviderLastName || '',
    };
  }

  // Same reader as the referral line, so a name split here and a name parsed there
  // cannot disagree about which word is the surname.
  const words = nameWords(String(draft.toProviderName || ''));
  if (words.length === 0) return { firstName: '', lastName: '' };
  if (words.length === 1) return { firstName: '', lastName: words[0] };
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
}

/**
 * The clinic From-provider that is the report's assigned doctor, or null.
 *
 * Names are written differently in the two places ("Veera Prasad Challa" vs
 * "Dr V CHALLA"), so match on surname plus first initial. Ambiguous → null, so the
 * sender picks rather than the wrong doctor being filled in.
 */
export function matchFromProvider<P extends { id: string; name?: string }>(
  providers: P[],
  assignedTo: string
): P | null {
  const a = nameWords(assignedTo).map(w => w.toLowerCase());
  if (a.length === 0) return null;
  const matches = providers.filter(p => {
    const w = nameWords(p.name || '').map(x => x.toLowerCase());
    if (w.length === 0 || w[w.length - 1] !== a[a.length - 1]) return false;
    if (w.length === 1 || a.length === 1) return true;
    return w[0][0] === a[0][0];
  });
  return matches.length === 1 ? matches[0] : null;
}

// MASTER SWITCH for the TXT/RTF (Voyager/RMI) distribution path in the UI.
// false = production: only PDF distribution is offered; the Text format option
// option is hidden. Backend/APIs/HL7 converter stay fully
// live — this only gates the UI. Flip to true once Voyager/MO approve TXT go-live.
const TXT_DISTRIBUTION_ENABLED = true;

export interface MoProvider {
  primaryId?: string;
  familyName?: string;
  givenName?: string;
  routable?: boolean;
  idType?: string;
  /** Practice/clinic name, derived by the backend from the MO STF record (XAD otherDesignation) */
  practiceName?: string;
  /** Street address ("street, CITY STATE postcode"), derived by the backend from the MO STF record */
  location?: string;
  _label?: string;
  [key: string]: any;
}

/** Optional patient/report summary shown at the top of the dialog */
export interface DistributionPatientInfo {
  name?: string;
  patientName?: string;
  dob?: string;
  sex?: string;
  age?: string | number;
  reportName?: string;
  date?: string;
  time?: string;
  studyDate?: string;
  studyTime?: string;
  modality?: string;
  patientId?: string;
  reportAssignedTo?: string;
  status?: string;
  referredBy?: string;
}

interface AddDistributionDialogProps {
  reportId: string;
  onClose: () => void;
  onSuccess: () => void;
  patientInfo?: DistributionPatientInfo;
  /** When provided, the dialog edits this existing DRAFT instead of creating new. */
  editDistribution?: {
    _id: string;
    /** 'medicalObject' or 'email' — decides which branch the dialog opens on. */
    type?: string;
    format?: string;
    fromProviderId?: string;
    fromProviderName?: string;
    toProviderId?: string;
    toProviderName?: string;
    toProviderFirstName?: string;
    toProviderLastName?: string;
    toIdType?: string;
    toEmail?: string;
    emailNote?: string;
  } | null;
}

type DistributionType = 'email' | 'mo';
type ReportFormat = 'pdf' | 'txt' | 'rtf';

const AddDistributionDialog: React.FC<AddDistributionDialogProps> = ({
  reportId,
  onClose,
  onSuccess,
  patientInfo,
  editDistribution,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [distributionType, setDistributionType] = useState<DistributionType>('mo');
  const [reportFormat, setReportFormat] = useState<ReportFormat>('pdf');

  // ── email distribution ────────────────────────────────────────────────────
  // The recipient is whatever is typed here: there is no directory to resolve it
  // against, and the address is stored verbatim on the distribution record because
  // that record is the only evidence of where the report went.
  const [emailTo, setEmailTo] = useState('');
  const [emailToName, setEmailToName] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  // Download of the selected format. Kept separate from the send flow: a
  // download must never imply the report was transmitted.
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Clinic fromProviders (pre-registered sending doctors)
  const [clinicFromProviders, setClinicFromProviders] = useState<Array<{ id: string; name?: string; idType?: string }>>([]);
  const [clinicId, setClinicId] = useState<string | undefined>(undefined);
  const [clinicFetchDone, setClinicFetchDone] = useState(false);
  const [clinicDefaultFormat, setClinicDefaultFormat] = useState<ReportFormat | null>(null);
  // Fixed recipient from Super Admin (e.g. RMI's routing ID RI48600004V).
  const [clinicDefaultRecipient, setClinicDefaultRecipient] = useState<string | null>(null);
  const [useManualFromProvider, setUseManualFromProvider] = useState(false);
  const [selectedFromProvider, setSelectedFromProvider] = useState<{ id: string; name?: string } | null>(null);

  // From Provider (lookup by ID)
  const [fromProviderInput, setFromProviderInput] = useState('');
  const [verifiedFromProvider, setVerifiedFromProvider] = useState<MoProvider | null>(null);
  const [fromVerifyError, setFromVerifyError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // To Provider (search by name or provider number)
  const [toSearchMode, setToSearchMode] = useState<'name' | 'number'>('name');
  const [toFirstName, setToFirstName] = useState('');
  const [toLastName, setToLastName] = useState('');
  const [toProviderNumber, setToProviderNumber] = useState('');
  const [searchResults, setSearchResults] = useState<MoProvider[]>([]);
  const [selectedToProvider, setSelectedToProvider] = useState<MoProvider | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  // Tracks that a by-number lookup ran and returned nothing — lets the user send
  // to an MO routing ID (e.g. RI48600004V) that MO hides from provider lookups
  // ("inactive but routable"). Per MO: put it in the addressing and it delivers.
  const [numberSearched, setNumberSearched] = useState(false);
  // MO's by-number lookup returns 404 for every number on our MO hosts, so an empty
  // result says nothing. MO's routability check does work by number; its answer
  // decides what the "not found" box says and whether "Use it directly" is offered.
  const [numberRoutability, setNumberRoutability] = useState<
    'routable' | 'not_routable' | 'invalid' | 'unknown' | null
  >(null);
  const [referringGpId, setReferringGpId] = useState<string | undefined>(undefined);

  const { loading: lookupByIdLoading } = useSelector(
    (state: RootState) => state.lookupProviderByIdResponse || { result: null, loading: false }
  );
  const { result: lookupResult, loading: lookupLoading } = useSelector(
    (state: RootState) => state.lookupProviderByNameResponse || { result: null, loading: false }
  );
  const { status: sendStatus, loading: sendLoading, msg: sendMsg } = useSelector(
    (state: RootState) => state.sendHL7Response || { status: '', loading: false, msg: '' }
  );
  const {
    status: saveDraftStatus,
    loading: saveDraftLoading,
    msg: saveDraftMsg,
  } = useSelector(
    (state: RootState) =>
      state.saveDraftDistributionResponse || { status: '', loading: false, msg: '' }
  );
  const {
    status: emailSendStatus,
    loading: emailSendLoading,
    msg: emailSendMsg,
  } = useSelector(
    (state: RootState) =>
      state.sendEmailDistributionResponse || { status: '', loading: false, msg: '' }
  );
  const {
    status: emailDraftStatus,
    loading: emailDraftLoading,
    msg: emailDraftMsg,
  } = useSelector(
    (state: RootState) => state.saveEmailDraftResponse || { status: '', loading: false, msg: '' }
  );

  useEffect(() => {
    if (lookupResult?.result && Array.isArray(lookupResult.result)) {
      setSearchResults(lookupResult.result);
    } else if (lookupResult?.result && !Array.isArray(lookupResult.result)) {
      setSearchResults([lookupResult.result]);
    } else {
      setSearchResults([]);
    }
  }, [lookupResult]);

  useEffect(() => {
    if (sendStatus === 'SUCCESS') {
      dispatch(clearSendHL7State());
      onSuccess();
    }
  }, [sendStatus, dispatch, onSuccess]);

  useEffect(() => {
    if (saveDraftStatus === 'SUCCESS') {
      dispatch(clearSaveDraftDistributionState());
      onSuccess();
    }
  }, [saveDraftStatus, dispatch, onSuccess]);

  useEffect(() => {
    if (emailSendStatus === 'SUCCESS') {
      dispatch(clearSendEmailDistributionState());
      onSuccess();
    }
  }, [emailSendStatus, dispatch, onSuccess]);

  useEffect(() => {
    if (emailDraftStatus === 'SUCCESS') {
      dispatch(clearSaveEmailDraftState());
      onSuccess();
    }
  }, [emailDraftStatus, dispatch, onSuccess]);

  /**
   * A stale FAILED message from a previous attempt must not sit under a form the user
   * has since corrected — it reads as if the new attempt failed too.
   */
  useEffect(() => {
    if (emailSendStatus === 'FAILED') {
      dispatch(clearSendEmailDistributionState());
    }
    if (emailDraftStatus === 'FAILED') {
      dispatch(clearSaveEmailDraftState());
    }
    // Only when the recipient actually changes, so the message stays visible until then.
  }, [emailTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch clinic fromProviders when dialog opens (for dropdown)
  useEffect(() => {
    if (!reportId || distributionType !== 'mo') return;
    const token = getToken();
    fetch(HL7.CLINIC_FROM_PROVIDERS(reportId), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.json())
      .then(json => {
        const list = json?.status === 'SUCCESS' && Array.isArray(json?.result?.fromProviders)
          ? json.result.fromProviders
          : [];
        setClinicFromProviders(list);
        if (list.length === 0) setUseManualFromProvider(true);
        if (json?.result?.clinicId) setClinicId(json.result.clinicId);
        const f = json?.result?.defaultFormat;
        if (f === 'pdf' || f === 'txt' || f === 'rtf') setClinicDefaultFormat(f);
        const r = String(json?.result?.defaultRecipientId || '').trim().toUpperCase();
        if (r) setClinicDefaultRecipient(r);
      })
      .catch(() => setClinicFromProviders([]))
      .finally(() => setClinicFetchDone(true));
  }, [reportId, distributionType]);

  // Edit mode: prefill from the existing DRAFT being edited (takes priority over
  // the clinic default below, which bails out once a recipient is set).
  useEffect(() => {
    if (!editDistribution) return;
    /**
     * An email draft opens the dialog on the Email branch with its recipient restored.
     * Without this, editing an email draft silently showed the MO form and the Save
     * would have rewritten the draft as an MO one.
     */
    if (editDistribution.type === 'email' || editDistribution.toEmail) {
      setDistributionType('email');
      setEmailTo(editDistribution.toEmail || '');
      setEmailToName(editDistribution.toProviderName || '');
      setEmailNote(editDistribution.emailNote || '');
      return;
    }
    if (editDistribution.format === 'txt' || editDistribution.format === 'pdf' || editDistribution.format === 'rtf') {
      setReportFormat(editDistribution.format);
    }
    if (editDistribution.fromProviderId) {
      setSelectedFromProvider({ id: editDistribution.fromProviderId, name: editDistribution.fromProviderName });
    }
    if (editDistribution.toProviderId) {
      setToSearchMode('number');
      setToProviderNumber(editDistribution.toProviderId);
      setNumberSearched(true);
      // Reopening a draft used to put the whole display name into the surname and blank
      // the given name, so editing a draft and sending it emitted
      // `4592703J^PRASANTH CHANDY^^^^DR` in PV1-8 — a surname no practice can match.
      // Sending the same draft from the row menu went through the backend, which split
      // the name, so the two paths produced different messages from one record.
      const stored = splitStoredProviderName(editDistribution);
      setSelectedToProvider({
        primaryId: editDistribution.toProviderId,
        familyName: stored.lastName,
        givenName: stored.firstName,
        idType: editDistribution.toIdType || undefined,
        routable: true,
        _label: editDistribution.toProviderName || 'Existing recipient',
      });
    }
  }, [editDistribution]);

  // Clinic's fixed recipient from Super Admin (auto distribution settings), e.g. a
  // routing ID to a practice-management system. Set directly: a routing ID does not
  // appear in provider lookup ("inactive but routable").
  useEffect(() => {
    // Never on an edited draft: it keeps its own recipient and format.
    if (!clinicDefaultRecipient || editDistribution || distributionType !== 'mo' || selectedToProvider) {
      return;
    }
    const isProviderNumber = AU_PROVIDER_NUMBER.test(clinicDefaultRecipient);
    setToSearchMode('number');
    setToProviderNumber(clinicDefaultRecipient);
    setNumberSearched(true);
    setSelectedToProvider({
      primaryId: clinicDefaultRecipient,
      familyName: '',
      givenName: '',
      idType: isProviderNumber ? 'AUSHICPR' : 'MO',
      routable: true,
      _label: isProviderNumber ? 'Provider number — sent directly' : 'Routing ID — sent directly',
    });
  }, [clinicDefaultRecipient, distributionType, selectedToProvider, editDistribution]);

  // A new distribution starts on the clinic's document type (Super Admin). An edited
  // draft keeps its own.
  useEffect(() => {
    if (editDistribution || !clinicDefaultFormat) return;
    setReportFormat(clinicDefaultFormat);
  }, [clinicDefaultFormat, editDistribution]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearLookupProviderByIdState());
      dispatch(clearLookupProviderState());
      // Otherwise a failure message from this dialog is still in the store the next
      // time it opens, on a different report.
      dispatch(clearSendEmailDistributionState());
      dispatch(clearSaveEmailDraftState());
    };
  }, [dispatch]);

  const splitReferralName = (raw: string): { firstName: string; lastName: string } => {
    if (raw.includes('^')) {
      const parts = raw.split('^');
      return { lastName: parts[0].trim(), firstName: parts[1]?.trim() || '' };
    }
    if (raw.includes(',')) {
      const [last, first = ''] = raw.split(',').map(s => s.trim());
      return { lastName: last, firstName: first };
    }
    const words = raw.replace(/^Dr\.?\s*/i, '').trim().split(/\s+/);
    return {
      lastName: words[words.length - 1] || raw,
      firstName: words.slice(0, -1).join(' '),
    };
  };

  // Auto-fill and search for referring GP once clinic fetch completes
  useEffect(() => {
    if (!clinicFetchDone || !patientInfo?.referredBy) return;
    const raw = patientInfo.referredBy.trim();
    if (!raw) return;

    // A clinic with a fixed recipient (e.g. RMI's routing ID) must not have the
    // referring-GP lookup take over the To Provider box. The GP still reaches PV1-8 by
    // name via referredBy on the backend.
    if (clinicDefaultRecipient) return;

    // Check for embedded MO provider ID: "Dr Name (284145AX)" → search by ID
    const idMatch = raw.match(/\(([A-Za-z0-9]+)\)\s*$/);
    if (idMatch) {
      const providerId = idMatch[1];
      const nameOnly = raw.replace(/\s*\([A-Za-z0-9]+\)\s*$/, '').trim();
      const { firstName, lastName } = splitReferralName(nameOnly);
      setToProviderNumber(providerId);
      setToSearchMode('number');
      setToFirstName(firstName);
      setToLastName(lastName);
      setReferringGpId(providerId);
      setIsSearching(true);
      dispatch(clearLookupProviderByIdState());
      lookupByNumber(providerId)
        .then(list => {
          if (list.length > 0) setSearchResults(list);
        })
        .finally(() => {
          setIsSearching(false);
          setNumberSearched(true);
        });
      return;
    }

    // DICOM PN Alphabetic or plain name — search by name
    const { firstName, lastName } = splitReferralName(raw);
    if (!lastName) return;
    setToFirstName(firstName);
    setToLastName(lastName);
    setToSearchMode('name');
    setIsSearching(true);
    dispatch(clearLookupProviderState());
    dispatch(
      lookupProviderByNameApi({
        familyName: lastName,
        givenName: firstName || undefined,
        clinicId,
      })
    ).then((action: any) => {
      if (lookupProviderByNameApi.fulfilled.match(action)) {
        const result = action.payload?.result;
        const list = Array.isArray(result) ? result : result ? [result] : [];
        if (list.length > 0 && list[0]?.primaryId) setReferringGpId(list[0].primaryId);
      }
    }).finally(() => setIsSearching(false));
  }, [clinicFetchDone, patientInfo?.referredBy]);

  // Auto-select From Provider matching the report's assigned doctor. Re-runs when the
  // assigned doctor arrives (it is fetched after the dialog can open). Never replaces a
  // provider already chosen or restored from a draft.
  useEffect(() => {
    if (!clinicFetchDone || selectedFromProvider || !patientInfo?.reportAssignedTo) return;
    const match = matchFromProvider(clinicFromProviders, patientInfo.reportAssignedTo);
    if (match) setSelectedFromProvider(match);
  }, [clinicFetchDone, clinicFromProviders, patientInfo?.reportAssignedTo]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Ask MO whether a number can receive. Sets numberRoutability; never throws. */
  const checkNumberRoutable = async (id: string) => {
    try {
      const token = getToken();
      const params = new URLSearchParams({ id, ...(clinicId ? { clinicId } : {}) });
      const res = await fetch(`${HL7.PROVIDER_IS_ROUTABLE}?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      const r = json?.status === 'SUCCESS' ? json.result : null;
      if (r?.invalid) setNumberRoutability('invalid');
      else if (!r || r.error) setNumberRoutability('unknown');
      else setNumberRoutability(r.routable ? 'routable' : 'not_routable');
    } catch {
      setNumberRoutability('unknown');
    }
  };

  /** Look a provider number up in MO; when nothing comes back, check routability. */
  const lookupByNumber = async (id: string): Promise<MoProvider[]> => {
    setNumberRoutability(null);
    const action: any = await dispatch(lookupProviderByIdApi({ id, clinicId }));
    if (lookupProviderByIdApi.fulfilled.match(action)) {
      const providers = action.payload?.result;
      const list = Array.isArray(providers) ? providers : providers ? [providers] : [];
      if (list.length > 0) return list;
    }
    await checkNumberRoutable(id);
    return [];
  };

  const handleVerifyFromProvider = async () => {
    const id = fromProviderInput.trim();
    if (!id) return;
    setIsVerifying(true);
    setVerifiedFromProvider(null);
    setFromVerifyError('');
    dispatch(clearLookupProviderByIdState());
    try {
      const resultAction = await dispatch(lookupProviderByIdApi({ id, clinicId }));
      if (lookupProviderByIdApi.fulfilled.match(resultAction)) {
        const providers = resultAction.payload?.result;
        const list = Array.isArray(providers) ? providers : providers ? [providers] : [];
        if (list.length > 0) {
          setVerifiedFromProvider(list[0]);
          setFromVerifyError('');
        } else {
          setFromVerifyError('Provider not found');
        }
      } else {
        setFromVerifyError((resultAction.payload as string) || 'Lookup failed');
      }
    } catch {
      setFromVerifyError('Lookup failed');
    } finally {
      setIsVerifying(false);
    }
  };

  // A search result can land below the visible part of the dialog. Scroll the
  // body to the very bottom, padding included, so it is fully in view.
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const scrollToOutcome = useRef(false);
  useEffect(() => {
    if (!scrollToOutcome.current || isSearching) return;
    // Results and the routability answer can arrive a render apart, so keep
    // following them briefly before letting go.
    const frame = requestAnimationFrame(() => {
      const body = dialogBodyRef.current;
      body?.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
    });
    const done = setTimeout(() => {
      scrollToOutcome.current = false;
    }, 600);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(done);
    };
  }, [isSearching, searchResults, numberRoutability]);

  const handleSearch = () => {
    if (!toLastName.trim()) return;
    scrollToOutcome.current = true;
    setIsSearching(true);
    setSelectedToProvider(null);
    dispatch(clearLookupProviderState());
    dispatch(
      lookupProviderByNameApi({
        familyName: toLastName.trim(),
        givenName: toFirstName.trim() || undefined,
        clinicId,
      })
    ).finally(() => setIsSearching(false));
  };

  const handleSearchById = () => {
    if (!toProviderNumber.trim()) return;
    scrollToOutcome.current = true;
    setIsSearching(true);
    setSelectedToProvider(null);
    setSearchResults([]);
    setNumberSearched(false);
    dispatch(clearLookupProviderByIdState());
    lookupByNumber(toProviderNumber.trim())
      .then(list => setSearchResults(list))
      .finally(() => {
        setIsSearching(false);
        setNumberSearched(true);
      });
  };

  // Use a typed provider/routing number directly as the recipient, bypassing the
  // provider directory. Needed for MO routing IDs (e.g. RI48600004V) that return
  // no lookup result but are routable — MO delivers when it's in the addressing.
  //
  // This used to hardcode idType 'MO' for anything typed here, because it was written
  // for routing IDs. But MO's by-number lookup 404s for plenty of legitimate Medicare
  // provider numbers too, so real GPs come through this path constantly — and every one
  // of them went out declared as a Medical-Objects directory id:
  //
  //   PV1-8: 4063782H^^^^^DR^^^Medical-Objects&7C3E3682-...&GUID
  //
  // The receiving practice then looks 4063782H up in the wrong namespace, finds nobody,
  // and files the report unallocated. It is the same failure the To Provider work was
  // meant to end, arriving by a different door. The id's own shape says which it is.
  const useNumberDirectly = () => {
    const id = toProviderNumber.trim();
    if (!id) return;
    const isProviderNumber = AU_PROVIDER_NUMBER.test(id);

    // A provider number identifies a person, so the message should carry that person's
    // name. It is not typed here, but the report already names the referring doctor with
    // their number — "Dr Anup Kumar (4063782H)". Taking the name only when that number
    // matches the one typed is corroboration, not the old scrape-and-hope: the number was
    // entered independently, and two independent sources agreeing on it is what makes the
    // name safe to use. No match, no name.
    const referred = isProviderNumber ? parseReferredBy(patientInfo?.referredBy) : null;
    const named = referred && referred.id === id ? referred : null;

    setSelectedToProvider({
      primaryId: id,
      familyName: named?.lastName || '',
      givenName: named?.firstName || '',
      idType: isProviderNumber ? 'AUSHICPR' : 'MO',
      routable: true,
      _label: isProviderNumber
        ? `Provider number — sent directly${named ? '' : ' (no name on file)'}`
        : 'Routing ID — sent directly',
    });
  };

  const effectiveFromId = selectedFromProvider?.id || verifiedFromProvider?.primaryId;
  const formReady =
    distributionType === 'mo' &&
    reportId &&
    effectiveFromId &&
    selectedToProvider?.primaryId;

  const handleDownload = async () => {
    setDownloadError('');
    setDownloadLoading(true);
    try {
      const result = await downloadReportFile({
        reportId,
        // Email always sends the PDF. Without this, switching from a TXT-format MO
        // setup to Email left the button downloading a TXT that is not what would be
        // attached — a preview that lies is worse than no preview.
        format: distributionType === 'email' ? 'pdf' : reportFormat,
        patientName: patientInfo?.patientName,
        reportName: patientInfo?.reportName,
        // So the download carries the same "Addressed to:" line the practice will get.
        // Email has no provider recipient, so it renders without one — which is exactly
        // what an emailed report looks like.
        recipient:
          distributionType === 'email' || !selectedToProvider?.primaryId
            ? null
            : {
                id: selectedToProvider.primaryId,
                lastName: selectedToProvider.familyName,
                firstName: selectedToProvider.givenName,
                idType: selectedToProvider.idType,
              },
      });
      if (!result.ok) {
        setDownloadError(result.error);
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  // Case-insensitive on purpose: the value reaches here from several screens and from
  // the report document itself, which is not guaranteed to be title-cased.
  const isFinalized = String(patientInfo?.status || '').toUpperCase() === 'FINALIZED';
  const canSubmit = formReady && !sendLoading && !saveDraftLoading;
  const canSend = canSubmit && isFinalized;
  const canSaveDraft = formReady && !sendLoading && !saveDraftLoading;

  // ── email branch ──────────────────────────────────────────────────────────
  /**
   * Deliberately permissive, and the same rule the server applies: real addresses vary
   * more than any strict pattern allows, and the sender can see what they typed. This
   * catches the mistake that actually happens (missing @, a space, no dot in the
   * domain) and nothing else.
   */
  const isEmail = distributionType === 'email';
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailTo.trim());
  const emailBusy = emailSendLoading || emailDraftLoading;
  const emailFormReady = !!reportId && emailValid && !emailBusy;
  const emailCanSend = emailFormReady && isFinalized;
  const emailShowError = emailTouched && !!emailTo.trim() && !emailValid;

  const emailPayload = () => ({
    reportId,
    toEmail: emailTo.trim(),
    ...(emailToName.trim() ? { toName: emailToName.trim() } : {}),
    ...(emailNote.trim() ? { note: emailNote.trim() } : {}),
    ...(editDistribution?._id ? { distributionId: editDistribution._id } : {}),
  });

  const handleEmailSend = async () => {
    if (!emailCanSend) return;
    try {
      await dispatch(sendEmailDistributionApi(emailPayload())).unwrap();
    } catch {
      // Surfaced via emailSendMsg — the server's own reason, kept verbatim.
    }
  };

  const handleEmailSaveDraft = async () => {
    if (!emailFormReady) return;
    try {
      await dispatch(saveEmailDraftApi(emailPayload())).unwrap();
    } catch {
      // Surfaced via emailDraftMsg.
    }
  };

  const buildSendingProvider = (): { id: string; lastName?: string; firstName?: string; prefix?: string; idType?: string } | undefined => {
    if (selectedFromProvider?.id) {
      const parts = (selectedFromProvider.name || '').trim().split(/\s+/);
      const lastName = parts.length > 1 ? parts[parts.length - 1] : (selectedFromProvider.name || '');
      const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
      return {
        id: selectedFromProvider.id,
        lastName: lastName || undefined,
        firstName: firstName || undefined,
        prefix: 'DR',
        idType: 'AUSHICPR'
      };
    }
    if (verifiedFromProvider?.primaryId) {
      return {
        id: verifiedFromProvider.primaryId,
        lastName: verifiedFromProvider.familyName || undefined,
        firstName: verifiedFromProvider.givenName || undefined,
        prefix: verifiedFromProvider.prefix || 'DR',
        idType: verifiedFromProvider.idType || 'AUSHICPR'
      };
    }
    return undefined;
  };

  const buildDraftPayload = () => {
    if (!selectedToProvider?.primaryId || !effectiveFromId) return null;
    const toFirst = selectedToProvider.givenName || '';
    const toLast = selectedToProvider.familyName || '';
    const toName = [toFirst, toLast].filter(Boolean).join(' ').trim();
    let fromProvider: { id: string; name?: string; idType?: string };
    if (selectedFromProvider?.id) {
      fromProvider = {
        id: selectedFromProvider.id,
        name: selectedFromProvider.name,
        idType: 'AUSHICPR',
      };
    } else if (verifiedFromProvider?.primaryId) {
      fromProvider = {
        id: verifiedFromProvider.primaryId,
        name: [verifiedFromProvider.givenName, verifiedFromProvider.familyName].filter(Boolean).join(' ').trim() || undefined,
        idType: verifiedFromProvider.idType || 'AUSHICPR',
      };
    } else {
      return null;
    }
    return {
      reportId,
      fromProvider,
      toProvider: {
        id: selectedToProvider.primaryId,
        name: toName || undefined,
        firstName: toFirst || undefined,
        lastName: toLast || undefined,
        ...(selectedToProvider.idType ? { idType: selectedToProvider.idType } : {}),
      },
      format: reportFormat,
      ...(editDistribution?._id ? { distributionId: editDistribution._id } : {}),
    };
  };

  const handleSaveDraft = async () => {
    if (!canSaveDraft || !selectedToProvider) return;
    const payload = buildDraftPayload();
    if (!payload) return;
    try {
      await dispatch(saveDraftDistributionApi(payload)).unwrap();
    } catch {
      // Error via saveDraftMsg
    }
  };

  const handleSubmit = async () => {
    if (!canSend || !selectedToProvider) return;
    const sendingProvider = buildSendingProvider();
    try {
      await dispatch(
        sendHL7Api({
          reportId,
          recipient: {
            id: selectedToProvider.primaryId,
            firstName: selectedToProvider.givenName || '',
            lastName: selectedToProvider.familyName || '',
            ...(selectedToProvider.idType ? { idType: selectedToProvider.idType } : {}),
          },
          ...(sendingProvider ? { sendingProvider } : {}),
          format: reportFormat,
        })
      ).unwrap();
    } catch {
      // Error shown via sendMsg in UI
    }
  };

  const displayProvider = (p: MoProvider) => {
    const given = p.givenName || (p.staffName && (p.staffName.givenName || p.staffName.GivenName)) || '';
    const family = p.familyName || (p.staffName && (p.staffName.familyName || p.staffName.FamilyName)) || '';
    const name = [given, family].filter(Boolean).join(' ');
    const id = p.primaryId || p.idNumber || p.id || (p.staffIDList && p.staffIDList[0] && (p.staffIDList[0].idNumber || p.staffIDList[0].id)) || '';
    const base = name ? `${name} (${id})` : String(id || '—');
    const where = [p.practiceName, p.location].filter(Boolean).join(', ');
    return where ? `${base} — ${where}` : base;
  };

  /**
   * A labelled value in the patient summary. Label and value are separate
   * lines so a long practice name or a two-part date wraps under its own
   * label instead of pushing the next column out of alignment — which is what
   * the old hand-rolled grid did.
   */
  const SummaryField: React.FC<{
    label: string;
    children: React.ReactNode;
    span?: boolean;
    color?: string;
  }> = ({ label, children, span, color }) => (
    <Box sx={{ minWidth: 0, ...(span ? { gridColumn: '1 / -1' } : null) }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: T.ink3,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 15, fontWeight: 600, color: color || T.ink, wordBreak: 'break-word' }}
      >
        {children}
      </Typography>
    </Box>
  );

  /** Section heading inside the form column. */
  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: T.ink2,
      }}
    >
      {children}
    </Typography>
  );

  const patientName = patientInfo?.name || patientInfo?.patientName || '—';
  const studyWhen = [
    patientInfo?.studyDate || patientInfo?.date,
    patientInfo?.studyTime || patientInfo?.time,
  ]
    .filter(Boolean)
    .join(' ');
  const sexAge = [patientInfo?.sex, patientInfo?.age]
    .filter(v => v !== undefined && v !== '')
    .join(' / ');

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-distribution-title"
      PaperProps={{
        sx: {
          backgroundColor: T.surface,
          backgroundImage: 'none',
          border: `1px solid ${T.line}`,
          maxHeight: '92vh',
        },
      }}
    >
      <DialogTitle
        id="add-distribution-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1.5,
          pb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: "'Spectral', serif",
              fontWeight: 600,
              fontSize: 22,
              display: 'block',
            }}
          >
            {editDistribution ? 'Edit Distribution' : 'Add Distribution'}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: T.ink3, mt: 0.25 }}>
            {distributionType === 'email'
              ? 'Email this report as a PDF attachment'
              : 'Send this report to a provider over Medical Objects'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ color: T.ink2, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent
        ref={dialogBodyRef}
        sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}
      >
        {/* ── patient & report summary ─────────────────────────────────── */}
        {patientInfo && (
          <Paper
            variant="outlined"
            sx={{
              px: 2,
              py: 1.75,
              // A LIFTED panel, not a darker one. T.well (#04080d) is almost
              // black and reads as a hole punched in the dialog rather than as
              // a grouped block of information.
              backgroundColor: 'rgba(255, 255, 255, 0.035)',
              borderColor: T.line,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                // Three columns so six fields fill two tidy rows. At two
                // columns an odd field count leaves a half-empty final row,
                // which is what made this look unbalanced.
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                columnGap: 3,
                rowGap: 1.75,
              }}
            >
              <SummaryField label="Patient">{patientName}</SummaryField>
              <SummaryField label="DOB">{patientInfo.dob || '—'}</SummaryField>
              {patientInfo.reportName && (
                <SummaryField label="Report">{patientInfo.reportName}</SummaryField>
              )}
              {studyWhen && <SummaryField label="Date">{studyWhen}</SummaryField>}
              {sexAge && <SummaryField label="Sex / Age">{sexAge}</SummaryField>}
              {patientInfo.patientId && (
                <SummaryField label="MRN">{patientInfo.patientId}</SummaryField>
              )}
              {patientInfo.referredBy && (
                <SummaryField
                  label="Referred by"
                  span
                  color={T.amber}
                >
                  {patientInfo.referredBy}
                  {referringGpId && !patientInfo.referredBy.includes(referringGpId) && (
                    <Typography
                      component="span"
                      sx={{ ml: 0.75, fontSize: 13, fontWeight: 400, color: T.ink2 }}
                    >
                      ({referringGpId})
                    </Typography>
                  )}
                </SummaryField>
              )}
            </Box>
          </Paper>
        )}

        {/* ── delivery type + format ───────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: distributionType === 'mo' ? '1fr 1fr' : '1fr' },
            gap: 2,
          }}
        >
          <TextField
            label="Type"
            select
            size="small"
            fullWidth
            value={distributionType}
            onChange={e => setDistributionType(e.target.value as DistributionType)}
          >
            <MenuItem value="mo">MO (Medical Objects)</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </TextField>

          {distributionType === 'mo' && (
            <TextField
              label="Report format"
              select
              size="small"
              fullWidth
              value={reportFormat}
              onChange={e => setReportFormat(e.target.value as ReportFormat)}
            >
              <MenuItem value="pdf">PDF (full layout, diagrams)</MenuItem>
              {/* TXT/RTF gated until Voyager/MO approve — see TXT_DISTRIBUTION_ENABLED */}
              {TXT_DISTRIBUTION_ENABLED && (
                <MenuItem value="txt">
                  Text (plain text, displays inline in practice software)
                </MenuItem>
              )}
              {TXT_DISTRIBUTION_ENABLED && (
                <MenuItem value="rtf">
                  RTF (formatted text with tables, for miSoft/Voyager)
                </MenuItem>
              )}
            </TextField>
          )}
        </Box>

        {distributionType === 'mo' && (
          <Box
            sx={{
              mt: -1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: T.ink3, flex: '1 1 260px', minWidth: 0 }}>
              {TXT_DISTRIBUTION_ENABLED && reportFormat === 'txt'
                ? 'Text format omits diagrams, images and table layout.'
                : TXT_DISTRIBUTION_ENABLED && reportFormat === 'rtf'
                  ? 'RTF preserves table layout for practice software that supports AUSPDI RTF display segments. Diagrams and images are omitted.'
                  : 'PDF carries the full report exactly as it appears on screen.'}
            </Typography>

            {/*
              Downloads the SAME file that would be transmitted, so it doubles as
              a way to check what a practice will actually receive before sending
              — which is how the RTF layout fault was diagnosed by hand.

              Only enabled on a finalized report, because all three preview
              endpoints refuse anything else. Disabling it is clearer than
              letting the click fail.
            */}
            <Button
              onClick={handleDownload}
              disabled={!isFinalized || downloadLoading}
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              title={
                isFinalized
                  ? `Download this report as ${reportFormat.toUpperCase()}`
                  : 'Report must be finalized before it can be downloaded'
              }
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {downloadLoading ? 'Preparing…' : `Download ${reportFormat.toUpperCase()}`}
            </Button>
          </Box>
        )}

        {downloadError && <Alert severity="error">{downloadError}</Alert>}

        {distributionType === 'email' ? (
          <>
            <Divider />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <TextField
                label="Recipient email"
                placeholder="name@practice.com.au"
                type="email"
                size="small"
                fullWidth
                required
                autoFocus
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                error={emailShowError}
                helperText={
                  emailShowError
                    ? 'That does not look like an email address.'
                    : 'The report PDF is attached to the email. Sent from an unmonitored address.'
                }
                inputProps={{ maxLength: 254, autoCapitalize: 'none', spellCheck: false }}
              />

              <TextField
                label="Recipient name (optional)"
                placeholder="Dr Jane Lee"
                size="small"
                fullWidth
                value={emailToName}
                onChange={e => setEmailToName(e.target.value)}
                helperText="Used to address the email and shown against this distribution."
                inputProps={{ maxLength: 120 }}
              />

              <TextField
                label="Note to the recipient (optional)"
                placeholder="Anything the recipient should read before opening the report."
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={emailNote}
                onChange={e => setEmailNote(e.target.value)}
                helperText={`${emailNote.length}/1000 — appears in the email body, not in the report.`}
                inputProps={{ maxLength: 1000 }}
              />

              {/*
                Same download the MO branch offers, and the same document the recipient
                receives — both are built by the one PDF path on the server. Lets the
                sender check the attachment before it leaves the building.
              */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12.5, color: T.ink3, flex: '1 1 240px', minWidth: 0 }}>
                  Email carries the full report as a PDF attachment, exactly as it appears on
                  screen. There is no delivery receipt for email — the record will show it as
                  sent, not as received.
                </Typography>
                <Button
                  onClick={handleDownload}
                  disabled={!isFinalized || downloadLoading}
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  title={
                    isFinalized
                      ? 'Download the PDF that will be attached'
                      : 'Report must be finalized before it can be downloaded'
                  }
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {downloadLoading ? 'Preparing…' : 'Download PDF'}
                </Button>
              </Box>

              {emailSendStatus === 'FAILED' && emailSendMsg && (
                <Alert severity="error">{emailSendMsg}</Alert>
              )}
              {emailDraftStatus === 'FAILED' && emailDraftMsg && (
                <Alert severity="error">{emailDraftMsg}</Alert>
              )}
            </Box>
          </>
        ) : (
          <>
            <Divider />

            {/* ── from provider ─────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {clinicFromProviders.length > 0 && !useManualFromProvider ? (
                <>
                  {/*
                    Labelled like Type and Report format rather than with a
                    separate section heading — a lone select under an uppercase
                    caption looked like a different kind of control.
                  */}
                  <TextField
                    label="From provider (your MO ID)"
                    select
                    size="small"
                    fullWidth
                    value={selectedFromProvider?.id ?? ''}
                    onChange={e => {
                      const val = e.target.value;
                      if (val === '__manual__') {
                        setUseManualFromProvider(true);
                        setSelectedFromProvider(null);
                      } else {
                        const p = clinicFromProviders.find(f => f.id === val);
                        setSelectedFromProvider(p ? { id: p.id, name: p.name } : null);
                        setVerifiedFromProvider(null);
                        setFromVerifyError('');
                      }
                    }}
                  >
                    <MenuItem value="">— Select provider —</MenuItem>
                    {clinicFromProviders.map(p => (
                      <MenuItem
                        key={p.id}
                        value={p.id}
                      >
                        {p.name ? `${p.name} (${p.id})` : p.id}
                      </MenuItem>
                    ))}
                    <MenuItem value="__manual__">Enter manually…</MenuItem>
                  </TextField>
                  {selectedFromProvider && (
                    <Alert
                      severity="success"
                      variant="outlined"
                    >
                      {selectedFromProvider.name
                        ? `${selectedFromProvider.name} (${selectedFromProvider.id})`
                        : selectedFromProvider.id}
                    </Alert>
                  )}
                </>
              ) : (
                <>
                  <SectionLabel>From provider (your MO ID)</SectionLabel>
                  {clinicFromProviders.length > 0 && (
                    <Button
                      onClick={() => {
                        setUseManualFromProvider(false);
                        setVerifiedFromProvider(null);
                        setFromProviderInput('');
                        setFromVerifyError('');
                      }}
                      size="small"
                      sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
                    >
                      ← Back to provider list
                    </Button>
                  )}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      value={fromProviderInput}
                      onChange={e => {
                        setFromProviderInput(e.target.value);
                        setVerifiedFromProvider(null);
                        setFromVerifyError('');
                      }}
                      placeholder="e.g. 202628VJ"
                      inputProps={{ 'aria-label': 'Your MO provider ID' }}
                    />
                    <Button
                      onClick={handleVerifyFromProvider}
                      disabled={!fromProviderInput.trim() || isVerifying || lookupByIdLoading}
                      variant="outlined"
                      sx={{ flexShrink: 0, minWidth: 104 }}
                    >
                      {isVerifying || lookupByIdLoading ? 'Verifying…' : 'Verify'}
                    </Button>
                  </Stack>
                  {verifiedFromProvider && (
                    <Alert
                      severity="success"
                      variant="outlined"
                    >
                      Verified: {displayProvider(verifiedFromProvider)}
                    </Alert>
                  )}
                  {fromVerifyError && <Alert severity="error">{fromVerifyError}</Alert>}
                </>
              )}
            </Box>

            {/* ── to provider ───────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <SectionLabel>To provider</SectionLabel>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={toSearchMode}
                onChange={(_e, mode) => {
                  // null arrives when the active button is clicked again; keep
                  // a mode selected rather than leaving the search unusable.
                  if (!mode) {
                    return;
                  }
                  setToSearchMode(mode);
                  setSearchResults([]);
                  setSelectedToProvider(null);
                  setNumberSearched(false);
                  setNumberRoutability(null);
                }}
                sx={{
                  alignSelf: 'flex-start',
                  // The default selected tint is nearly invisible on this dark
                  // surface; the old code faked the same problem with opacity.
                  '& .MuiToggleButton-root.Mui-selected': {
                    backgroundColor: 'rgba(45, 212, 191, 0.18)',
                    color: T.teal,
                    '&:hover': { backgroundColor: 'rgba(45, 212, 191, 0.24)' },
                  },
                }}
              >
                <ToggleButton value="name">By name</ToggleButton>
                <ToggleButton value="number">By provider number</ToggleButton>
              </ToggleButtonGroup>

              {toSearchMode === 'name' ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                >
                  <TextField
                    size="small"
                    fullWidth
                    value={toFirstName}
                    onChange={e => setToFirstName(e.target.value)}
                    label="First name"
                  />
                  <TextField
                    size="small"
                    fullWidth
                    required
                    value={toLastName}
                    onChange={e => setToLastName(e.target.value)}
                    label="Last name"
                  />
                  <Button
                    onClick={handleSearch}
                    disabled={!toLastName.trim() || isSearching || lookupLoading}
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    sx={{ flexShrink: 0, minWidth: 124 }}
                  >
                    {lookupLoading || isSearching ? 'Searching…' : 'Search'}
                  </Button>
                </Stack>
              ) : (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                >
                  <TextField
                    size="small"
                    fullWidth
                    value={toProviderNumber}
                    onChange={e => {
                      setToProviderNumber(e.target.value);
                      setNumberSearched(false);
                      setNumberRoutability(null);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSearchById();
                      }
                    }}
                    label="Provider number"
                    placeholder="e.g. 33333BY"
                  />
                  <Button
                    onClick={handleSearchById}
                    disabled={!toProviderNumber.trim() || isSearching || lookupByIdLoading}
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    sx={{ flexShrink: 0, minWidth: 124 }}
                  >
                    {lookupByIdLoading || isSearching ? 'Searching…' : 'Search'}
                  </Button>
                </Stack>
              )}

              {toSearchMode === 'number' &&
                numberSearched &&
                !isSearching &&
                searchResults.length === 0 &&
                toProviderNumber.trim() &&
                !selectedToProvider &&
                (() => {
                  const id = toProviderNumber.trim();
                  const useDirectly = (
                    <Button
                      onClick={useNumberDirectly}
                      size="small"
                      color="inherit"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Use it directly
                    </Button>
                  );
                  if (numberRoutability === 'routable') {
                    return (
                      <Alert
                        severity="success"
                        action={useDirectly}
                      >
                        <strong>{id}</strong> is on the Medical Objects network and can receive
                        reports. MO does not return the doctor&apos;s details for a number, so it
                        is sent directly.
                      </Alert>
                    );
                  }
                  if (numberRoutability === 'not_routable') {
                    return (
                      <Alert severity="warning">
                        <strong>{id}</strong> is not registered on the Medical Objects network. A
                        send to it would be refused. Check the number, or search by name.
                      </Alert>
                    );
                  }
                  if (numberRoutability === 'invalid') {
                    return (
                      <Alert severity="error">
                        <strong>{id}</strong> is not a valid provider number.
                      </Alert>
                    );
                  }
                  return (
                    <Alert
                      severity="info"
                      action={useDirectly}
                    >
                      Could not check <strong>{id}</strong> with Medical Objects. If you know this
                      number is right, send to it directly.
                    </Alert>
                  );
                })()}

              {searchResults.length > 0 && (
                <>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: T.line,
                      backgroundColor: 'rgba(255, 255, 255, 0.035)',
                      overflow: 'hidden',
                    }}
                  >
                    <List
                      disablePadding
                      sx={{ maxHeight: 260, overflowY: 'auto' }}
                    >
                      {searchResults.map((p, idx) => (
                        <ListItemButton
                          key={p.primaryId || idx}
                          selected={selectedToProvider?.primaryId === p.primaryId}
                          onClick={() => setSelectedToProvider(p)}
                          sx={{
                            display: 'block',
                            py: 1,
                            borderBottom:
                              idx < searchResults.length - 1 ? `1px solid ${T.line}` : 'none',
                            '&.Mui-selected': { backgroundColor: 'rgba(45, 212, 191, 0.14)' },
                            '&.Mui-selected:hover': { backgroundColor: 'rgba(45, 212, 191, 0.2)' },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                              {[
                                p.givenName || p.staffName?.givenName,
                                p.familyName || p.staffName?.familyName,
                              ]
                                .filter(Boolean)
                                .join(' ') || '—'}
                            </Typography>
                            {(p.primaryId || p.idNumber) && (
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  letterSpacing: '0.03em',
                                  color: T.teal,
                                }}
                              >
                                {p.primaryId || p.idNumber}
                              </Typography>
                            )}
                            {p.practiceName && (
                              <Typography sx={{ fontSize: 13.5, color: T.ink2 }}>
                                — {p.practiceName}
                              </Typography>
                            )}
                            {/*
                              Routability is stated in words, not by colour alone —
                              a non-routable provider silently fails to receive.
                            */}
                            {p.routable === false && (
                              <Typography sx={{ fontSize: 12, color: T.amber }}>
                                (not routable)
                              </Typography>
                            )}
                          </Box>
                          {p.location && (
                            <Typography sx={{ fontSize: 13, color: T.ink3, mt: 0.25 }}>
                              {p.location}
                            </Typography>
                          )}
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                  <Typography sx={{ fontSize: 12, color: T.ink3, alignSelf: 'flex-end' }}>
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  </Typography>
                </>
              )}

              {selectedToProvider && (
                <Alert
                  severity="success"
                  variant="outlined"
                >
                  {[selectedToProvider.givenName, selectedToProvider.familyName]
                    .filter(Boolean)
                    .join(' ') ||
                    selectedToProvider._label ||
                    '—'}
                  {selectedToProvider.primaryId && (
                    <Typography
                      component="span"
                      sx={{ ml: 0.75, fontWeight: 700 }}
                    >
                      ({selectedToProvider.primaryId})
                    </Typography>
                  )}
                  {(selectedToProvider.practiceName || selectedToProvider.location) && (
                    <Typography
                      component="span"
                      sx={{ ml: 0.75, color: T.ink2 }}
                    >
                      —{' '}
                      {[selectedToProvider.practiceName, selectedToProvider.location]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          </>
        )}

        {sendStatus === 'FAILED' && <Alert severity="error">{sendMsg}</Alert>}
        {saveDraftStatus === 'FAILED' && <Alert severity="error">{saveDraftMsg}</Alert>}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {/*
          The blocking reason sits on the same row as the buttons it explains,
          left-aligned, rather than dangling under them.
        */}
        {!isFinalized && (
          <Typography sx={{ fontSize: 12.5, color: T.amber, mr: 'auto', flex: '1 1 200px' }}>
            Report must be finalized before sending
          </Typography>
        )}
        <Button onClick={onClose}>Cancel</Button>
        {/*
          One pair of buttons, wired to whichever channel is selected. Two separate
          sets would be the obvious alternative and the wrong one: the enabled/disabled
          reasoning would then live in two places and drift.
        */}
        <Button
          onClick={isEmail ? handleEmailSaveDraft : handleSaveDraft}
          disabled={isEmail ? !emailFormReady : !canSaveDraft}
          variant="outlined"
        >
          {(isEmail ? emailDraftLoading : saveDraftLoading) ? 'Saving…' : 'Save'}
        </Button>
        <Button
          onClick={isEmail ? handleEmailSend : handleSubmit}
          disabled={isEmail ? !emailCanSend : !canSend}
          variant="contained"
        >
          {(isEmail ? emailSendLoading : sendLoading) ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDistributionDialog;
